/**
 * Authentication API
 * Handles QR code login and browser OAuth flows
 */

import { createServer, type Server } from 'http';
import open from 'open';
import qrcode from 'qrcode-terminal';
import { getPublicApiClient, getApiClient, resetApiClients } from './client.js';
import { storeAuth, clearAuth, getWebUrl } from '../utils/config.js';
import * as output from '../utils/output.js';

export interface QrSessionResponse {
  success: boolean;
  sessionId: string;
  expiresAt: string;
  expiresInSeconds: number;
  qrContent: string;
}

export interface QrStatusResponse {
  success: boolean;
  status: 'pending' | 'scanned' | 'confirmed' | 'expired' | 'used';
  token?: string;
  userId?: number;
  isExpired?: boolean;
  expiresAt?: string;
}

export interface LoginResponse {
  user: {
    id: number;
    email: string;
    username?: string;
    displayName?: string;
  };
  token: string;
}

export interface UserInfo {
  id: number;
  email: string;
  name?: string;
  picture?: string;
}

/**
 * Create a QR login session
 */
export async function createQrSession(deviceType = 'CLI'): Promise<QrSessionResponse> {
  const client = getPublicApiClient();
  const response = await client.post<QrSessionResponse>('/auth/qr/create-session', {
    deviceType,
    deviceId: `cli-${Date.now()}`,
  });
  return response.data;
}

/**
 * Check QR session status
 */
export async function getQrSessionStatus(sessionId: string): Promise<QrStatusResponse> {
  const client = getPublicApiClient();
  const response = await client.get<QrStatusResponse>(`/auth/qr/status/${sessionId}`);
  return response.data;
}

/**
 * Perform QR code login
 * Displays QR code in terminal and polls for confirmation
 */
export async function qrLogin(
  onStatus?: (status: string) => void
): Promise<{ success: boolean; error?: string }> {
  try {
    // Create QR session
    const session = await createQrSession();

    // Display QR code in terminal
    output.newline();
    output.info('Scan this QR code with the Paean mobile app to log in:');
    output.newline();

    qrcode.generate(session.qrContent, { small: true });

    output.newline();
    output.dim(`Session expires in ${Math.floor(session.expiresInSeconds / 60)} minutes`);
    output.newline();

    // Poll for status
    const maxPollTime = session.expiresInSeconds * 1000;
    const pollInterval = 2000;
    const startTime = Date.now();

    while (Date.now() - startTime < maxPollTime) {
      const status = await getQrSessionStatus(session.sessionId);

      if (onStatus) {
        onStatus(status.status);
      }

      switch (status.status) {
        case 'scanned':
          output.info('QR code scanned! Waiting for confirmation...');
          break;

        case 'confirmed':
          if (status.token && status.userId) {
            // Store authentication
            storeAuth({
              token: status.token,
              userId: status.userId,
            });
            resetApiClients();

            // Try to fetch user info
            try {
              const userInfo = await getCurrentUser();
              if (userInfo) {
                storeAuth({
                  token: status.token,
                  userId: status.userId,
                  email: userInfo.email,
                });
              }
            } catch {
              // Ignore - we still have the token
            }

            return { success: true };
          }
          return { success: false, error: 'No token received' };

        case 'expired':
          return { success: false, error: 'QR code expired. Please try again.' };

        case 'used':
          return { success: false, error: 'This QR code has already been used.' };

        case 'pending':
        default:
          // Continue polling
          break;
      }

      // Wait before next poll
      await new Promise((resolve) => setTimeout(resolve, pollInterval));
    }

    return { success: false, error: 'Login timed out. Please try again.' };
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Unknown error';
    return { success: false, error: message };
  }
}

/**
 * Perform browser-based OAuth login
 * Opens browser and starts local server to receive callback
 */
export async function browserLogin(): Promise<{ success: boolean; error?: string }> {
  return new Promise((resolve) => {
    // Find an available port
    const port = 9876 + Math.floor(Math.random() * 100);
    const callbackUrl = `http://127.0.0.1:${port}/callback`;

    let server: Server | null = null;
    let timeoutId: NodeJS.Timeout | null = null;
    let isResolved = false;

    const safeResolve = (result: { success: boolean; error?: string }) => {
      if (isResolved) return;
      isResolved = true;
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
      if (server) {
        // Give pending requests time to complete before closing
        setTimeout(() => {
          if (server) {
            server.close();
            server = null;
          }
        }, 500);
      }
      resolve(result);
    };

    // CORS headers for all responses
    const corsHeaders: Record<string, string> = {
      'Access-Control-Allow-Origin': '*',
      'Access-Control-Allow-Methods': 'GET, OPTIONS, POST',
      'Access-Control-Allow-Headers': 'Content-Type',
    };

    // Create local server to receive callback
    server = createServer((req, res) => {
      const method = req.method || 'GET';

      // Handle CORS preflight (OPTIONS) — browser sends this before POST
      if (method === 'OPTIONS') {
        res.writeHead(204, { ...corsHeaders, 'Content-Length': '0' });
        res.end();
        return;
      }

      // Parse URL manually to avoid quirks
      const rawUrl = req.url || '/';
      const pathEnd = rawUrl.indexOf('?');
      const pathname = pathEnd === -1 ? rawUrl : rawUrl.substring(0, pathEnd);
      const search = pathEnd === -1 ? '' : rawUrl.substring(pathEnd);

      if (pathname !== '/callback') {
        res.writeHead(404);
        res.end('Not found');
        return;
      }

      // Process callback with token and other params
      const processCallback = (
        token?: string,
        error?: string,
        userId?: string,
        email?: string,
      ) => {
        if (token) {
          // Store authentication
          storeAuth({
            token,
            userId: userId ? parseInt(userId, 10) : undefined,
            email: email || undefined,
          });
          resetApiClients();

          // POST: return JSON; GET: return HTML
          if (method === 'POST') {
            const json = JSON.stringify({ success: true, message: 'Login successful' });
            res.writeHead(200, {
              'Content-Type': 'application/json',
              'Content-Length': Buffer.byteLength(json),
              'Connection': 'close',
              ...corsHeaders,
            });
            res.end(json, () => safeResolve({ success: true }));
          } else {
            res.writeHead(200, { 'Content-Type': 'text/html', ...corsHeaders });
            res.end(`<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>Authorized — Paean</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif; min-height: 100vh; display: flex; align-items: center; justify-content: center; background: #0a0a0a; color: #fff; }
    .container { text-align: center; max-width: 420px; padding: 48px 32px; }
    .icon-ring { display: inline-flex; align-items: center; justify-content: center; width: 80px; height: 80px; border-radius: 50%; background: rgba(34, 197, 94, 0.08); border: 2px solid rgba(34, 197, 94, 0.2); margin-bottom: 24px; }
    .icon-ring svg { width: 40px; height: 40px; color: #22c55e; }
    h1 { font-size: 24px; font-weight: 700; letter-spacing: -0.02em; margin-bottom: 8px; color: #fafafa; }
    .subtitle { font-size: 15px; color: #737373; line-height: 1.5; margin-bottom: 32px; }
    .user-badge { display: inline-flex; align-items: center; gap: 8px; padding: 8px 16px; background: #171717; border: 1px solid #262626; border-radius: 100px; margin-bottom: 32px; }
    .user-dot { width: 8px; height: 8px; border-radius: 50%; background: #22c55e; }
    .user-email { font-size: 13px; color: #a3a3a3; }
    .hint { font-size: 12px; color: #525252; }
  </style>
</head>
<body>
  <div class="container">
    <div class="icon-ring">
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" /></svg>
    </div>
    <h1>You're all set</h1>
    <p class="subtitle">Your device has been authorized successfully.</p>
    ${email ? `<div class="user-badge"><span class="user-dot"></span><span class="user-email">${email}</span></div><br>` : ''}
    <p class="hint">You can close this tab and return to your terminal.</p>
  </div>
</body>
</html>`, () => safeResolve({ success: true }));
          }
        } else {
          const message = error || 'No token received from server';
          if (method === 'POST') {
            const json = JSON.stringify({ success: false, error: message });
            res.writeHead(400, {
              'Content-Type': 'application/json',
              'Content-Length': Buffer.byteLength(json),
              'Connection': 'close',
              ...corsHeaders,
            });
            res.end(json, () => safeResolve({ success: false, error: message }));
          } else {
            res.writeHead(200, { 'Content-Type': 'text/html', ...corsHeaders });
            res.end(`<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>Authorization Failed — Paean</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif; min-height: 100vh; display: flex; align-items: center; justify-content: center; background: #0a0a0a; color: #fff; }
    .container { text-align: center; max-width: 420px; padding: 48px 32px; }
    .icon-ring { display: inline-flex; align-items: center; justify-content: center; width: 80px; height: 80px; border-radius: 50%; background: rgba(239, 68, 68, 0.08); border: 2px solid rgba(239, 68, 68, 0.2); margin-bottom: 24px; }
    .icon-ring svg { width: 40px; height: 40px; color: #ef4444; }
    h1 { font-size: 24px; font-weight: 700; letter-spacing: -0.02em; margin-bottom: 8px; color: #fafafa; }
    .subtitle { font-size: 15px; color: #737373; line-height: 1.5; margin-bottom: 12px; }
    .error-msg { font-size: 13px; color: #f87171; background: rgba(239, 68, 68, 0.08); border: 1px solid rgba(239, 68, 68, 0.15); border-radius: 12px; padding: 12px 16px; margin-bottom: 32px; }
    .hint { font-size: 12px; color: #525252; }
  </style>
</head>
<body>
  <div class="container">
    <div class="icon-ring">
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12" /></svg>
    </div>
    <h1>Authorization Failed</h1>
    <p class="subtitle">We couldn't complete the sign-in.</p>
    <p class="error-msg">${message}</p>
    <p class="hint">Please return to your terminal and try again.</p>
  </div>
</body>
</html>`, () => safeResolve({ success: false, error: message }));
          }
        }
      };

      // Get token: support both POST (body, preferred) and GET (query)
      let token: string | undefined;
      let error: string | undefined;
      let userId: string | undefined;
      let email: string | undefined;

      if (method === 'POST') {
        // Read POST body
        let body = '';
        req.on('data', (chunk: Buffer) => {
          body += chunk.toString('utf-8');
        });
        req.on('end', () => {
          try {
            // Try JSON first
            const json = JSON.parse(body);
            token = json.token;
            error = json.error;
            userId = json.userId;
            email = json.email;
          } catch {
            // Try form-encoded
            body.split('&').forEach(pair => {
              const [key, val] = pair.split('=');
              if (key && val !== undefined) {
                const decoded = decodeURIComponent(val);
                if (key === 'token') token = decoded;
                else if (key === 'error') error = decoded;
                else if (key === 'userId') userId = decoded;
                else if (key === 'email') email = decoded;
              }
            });
          }
          // Process after body is read
          processCallback(token, error, userId, email);
        });
        return;  // Don't process until body is read
      }

      // GET: parse from query params
      if (search.length > 1) {
        search.substring(1).split('&').forEach(pair => {
          const [key, val] = pair.split('=');
          if (key && val !== undefined) {
            const decoded = decodeURIComponent(val);
            if (key === 'token') token = decoded;
            else if (key === 'error') error = decoded;
            else if (key === 'userId') userId = decoded;
            else if (key === 'email') email = decoded;
          }
        });
      }
      processCallback(token, error, userId, email);
    });

    server.on('error', (err) => {
      safeResolve({ success: false, error: `Server error: ${err.message}` });
    });

    // Explicitly bind to 127.0.0.1 to avoid IPv6/localhost issues
    server.listen(port, '127.0.0.1', async () => {
      // Open browser to login page
      const webUrl = getWebUrl();
      const loginUrl = `${webUrl}/auth/cli?callback=${encodeURIComponent(callbackUrl)}`;

      output.info('Opening browser for login...');
      output.dim(`If browser doesn't open, visit: ${loginUrl}`);
      output.newline();

      try {
        await open(loginUrl);
      } catch {
        output.warning('Could not open browser automatically.');
        output.info(`Please open this URL manually: ${loginUrl}`);
      }

      output.info('Waiting for login...');

      // Set timeout (5 minutes)
      timeoutId = setTimeout(() => {
        safeResolve({ success: false, error: 'Login timed out. Please try again.' });
      }, 5 * 60 * 1000);
    });
  });
}

/**
 * Get current user info
 */
export async function getCurrentUser(): Promise<UserInfo | null> {
  try {
    const client = getApiClient();
    const response = await client.get<{ user: UserInfo } | UserInfo>('/user/profile');

    // Handle both response formats
    const data = response.data;
    if ('user' in data) {
      return data.user;
    }
    return data;
  } catch {
    return null;
  }
}

/**
 * Validate current token
 */
export async function validateToken(): Promise<boolean> {
  try {
    const user = await getCurrentUser();
    return user !== null;
  } catch {
    return false;
  }
}

/**
 * Get the server's public key for RSA encryption
 */
async function getPublicKey(): Promise<string> {
  const client = getPublicApiClient();
  const response = await client.get<{ publicKey?: string; key?: string }>('/auth/public-key');
  return response.data.publicKey || response.data.key || '';
}

/**
 * Encrypt password with RSA public key
 * Falls back to plain text if crypto not available
 */
async function encryptPassword(password: string, publicKey: string): Promise<string> {
  if (!publicKey) return password;

  try {
    const crypto = await import('crypto');
    const buffer = Buffer.from(password, 'utf-8');
    const encrypted = crypto.publicEncrypt(
      {
        key: publicKey,
        padding: crypto.constants.RSA_PKCS1_OAEP_PADDING,
        oaepHash: 'sha256',
      },
      buffer
    );
    return encrypted.toString('base64');
  } catch {
    // Fall back to plain text if encryption fails
    return password;
  }
}

/**
 * Login with email and password
 */
export async function emailLogin(
  email: string,
  password: string,
): Promise<{ success: boolean; error?: string }> {
  try {
    // Get public key for password encryption
    let encryptedPassword = password;
    try {
      const publicKey = await getPublicKey();
      if (publicKey) {
        encryptedPassword = await encryptPassword(password, publicKey);
      }
    } catch {
      // Server may not support public key endpoint - send plain
    }

    const client = getPublicApiClient();
    const response = await client.post<{
      token?: string;
      refreshToken?: string;
      user?: { id: number; email: string };
      expiresAt?: string;
    }>('/auth/email', {
      email,
      password: encryptedPassword,
    });

    const data = response.data;
    if (data.token) {
      storeAuth({
        token: data.token,
        refreshToken: data.refreshToken,
        userId: data.user?.id,
        email: data.user?.email || email,
        expiresAt: data.expiresAt,
      });
      resetApiClients();
      return { success: true };
    }

    return { success: false, error: 'No token received' };
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Unknown error';
    return { success: false, error: message };
  }
}

/**
 * Login with wallet private key (EVM or Solana).
 * Performs the full nonce-request -> sign -> login flow.
 */
export async function walletLogin(
  chain: 'evm' | 'solana',
  privateKey: string,
): Promise<{ success: boolean; error?: string; address?: string }> {
  try {
    const publicClient = getPublicApiClient();
    let address: string;
    let signFn: (message: string) => Promise<string>;

    if (chain === 'evm') {
      const { ethers } = await import('ethers');
      const wallet = new ethers.Wallet(privateKey);
      address = wallet.address;
      signFn = async (msg: string) => wallet.signMessage(msg);
    } else {
      const nacl = await import('tweetnacl');
      const { Keypair } = await import('@solana/web3.js');
      const keyBytes = Buffer.from(privateKey, 'base64');
      const keypair = Keypair.fromSecretKey(keyBytes);
      address = keypair.publicKey.toBase58();
      signFn = async (msg: string) => {
        const messageBytes = new TextEncoder().encode(msg);
        const sig = nacl.default.sign.detached(messageBytes, keypair.secretKey);
        return Buffer.from(sig).toString('base64');
      };
    }

    // Step 1: Request nonce
    const nonceRes = await publicClient.post<{
      nonce: string;
      message: string;
      expiresAt: string;
    }>('/auth/wallet/nonce', { address, chain });

    const { message } = nonceRes.data;

    // Step 2: Sign the message
    const signature = await signFn(message);

    // Step 3: Login
    const loginRes = await publicClient.post<{
      user: { id: number; email: string; token: string };
      token: string;
      isNewUser: boolean;
    }>('/auth/wallet/login', { address, signature, chain });

    const data = loginRes.data;
    const token = data.token || data.user?.token;

    if (token) {
      storeAuth({
        token,
        userId: data.user?.id,
        email: data.user?.email,
      });
      resetApiClients();
      return { success: true, address };
    }

    return { success: false, error: 'No token received' };
  } catch (error: unknown) {
    if (error && typeof error === 'object' && 'response' in error) {
      const axiosErr = error as { response?: { data?: { error?: string } } };
      const msg = axiosErr.response?.data?.error;
      if (msg) return { success: false, error: msg };
    }
    const message = error instanceof Error ? error.message : 'Unknown error';
    return { success: false, error: message };
  }
}

/**
 * Logout - clear stored credentials
 */
export function logout(): void {
  clearAuth();
  resetApiClients();
}
